import { Injectable, NgZone, OnDestroy, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const WARNING_DURATION_MS = 3 * 60 * 1000;
const WARNING_AFTER_MS = IDLE_TIMEOUT_MS - WARNING_DURATION_MS;
const ACTIVITY_EVENTS = ['click', 'keydown', 'pointerdown', 'scroll', 'touchstart', 'wheel'];

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  readonly warningOpen = signal(false);
  readonly remainingSeconds = signal(WARNING_DURATION_MS / 1000);
  readonly remainingLabel = computed(() => {
    const total = Math.max(0, this.remainingSeconds());
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  });

  private readonly routeSub: Subscription;
  private listening = false;
  private authenticated = false;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private warningDeadline = 0;

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {
    this.routeSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.syncAuthState();
    });
    this.syncAuthState();
  }

  ngOnDestroy(): void {
    this.stop();
    this.routeSub.unsubscribe();
  }

  staySignedIn(): void {
    this.stopTimers();
    this.warningOpen.set(false);

    firstValueFrom(this.auth.fetchMe())
      .then(() => {
        this.authenticated = true;
        this.scheduleWarning();
      })
      .catch(() => this.auth.finishLogout('session-expired'));
  }

  logoutNow(): void {
    void firstValueFrom(this.auth.logout('logout'));
  }

  private syncAuthState(): void {
    const authenticated = this.auth.isAuthenticated() && !this.isAuthRoute(this.router.url);
    this.authenticated = authenticated;
    if (authenticated) {
      this.start();
    } else {
      this.stopTimers();
      this.warningOpen.set(false);
    }
  }

  private start(): void {
    if (typeof window === 'undefined') return;
    if (!this.listening) {
      this.zone.runOutsideAngular(() => {
        ACTIVITY_EVENTS.forEach((eventName) => {
          window.addEventListener(eventName, this.handleActivity, { passive: true });
        });
      });
      this.listening = true;
    }
    this.scheduleWarning();
  }

  private stop(): void {
    if (typeof window !== 'undefined' && this.listening) {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, this.handleActivity));
    }
    this.listening = false;
    this.stopTimers();
  }

  private readonly handleActivity = (): void => {
    if (!this.authenticated || this.warningOpen()) return;
    this.scheduleWarning();
  };

  private scheduleWarning(): void {
    this.stopTimers();
    if (!this.authenticated || typeof window === 'undefined') return;
    this.warningTimer = window.setTimeout(() => {
      this.zone.run(() => this.openWarning());
    }, WARNING_AFTER_MS);
  }

  private openWarning(): void {
    if (!this.authenticated) return;
    this.warningOpen.set(true);
    this.remainingSeconds.set(WARNING_DURATION_MS / 1000);
    this.warningDeadline = Date.now() + WARNING_DURATION_MS;

    this.countdownTimer = setInterval(() => {
      const remaining = Math.ceil((this.warningDeadline - Date.now()) / 1000);
      this.remainingSeconds.set(Math.max(0, remaining));
    }, 1000);

    this.logoutTimer = setTimeout(() => this.expireSession(), WARNING_DURATION_MS);
  }

  private expireSession(): void {
    this.stopTimers();
    this.warningOpen.set(false);
    this.authenticated = false;
    firstValueFrom(this.auth.logout('idle-timeout')).catch(() => this.auth.finishLogout('idle-timeout'));
  }

  private stopTimers(): void {
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.logoutTimer) clearTimeout(this.logoutTimer);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.warningTimer = null;
    this.logoutTimer = null;
    this.countdownTimer = null;
  }

  private isAuthRoute(url: string): boolean {
    const path = (url || '').split('?')[0];
    return path === '/login' || path === '/sso' || path.startsWith('/sso/') || path === '/superadmin/login';
  }
}
